import axios, { AxiosInstance } from 'axios'
import {
  UsersApi,
  EventsApi,
  UsersApiInterface,
  DashboardRolesApi,
  DashboardRolesApiInterface,
  CategoriesActivitiesApi,
  CategoriesActivitiesApiInterface,
  Configuration,
  EventsApiInterface,
  ExportApiInterface,
  ExportApi
} from '../services/network'
import { IMultipartServicesApi, MultipartServicesApi } from '../services/network-persistent/api/multipart-services-api'
import { store } from '../store/store'
import dayjs from 'dayjs'
import { log } from './logger'
import { auth } from '../firebaseConfig'
import { clearToken, setToken } from '../store/slices/authSlice'

const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080'

let token: string = store.getState().auth.token ?? ''
store.subscribe(() => {
  token = store.getState().auth.token ?? ''
  const expirationTime = store.getState().auth.expirationTime ?? dayjs().toISOString()
  log.debug(
    `[subscribe] new token ${token.substring(token.length - 20)} expiring at ${dayjs(expirationTime).format(`YYYY-MM-DD HH:mm:ss`)} (${expirationTime} UTC) (now: ${dayjs().format(`YYYY-MM-DD HH:mm:ss`)})`
  )
})

async function refreshFirebaseToken(): Promise<{ token: string; expirationTime: string }> {
  const tokenResult = await auth.currentUser?.getIdTokenResult(true)
  if (!tokenResult) {
    return { token: '', expirationTime: dayjs().toISOString() }
  }
  const token = tokenResult.token
  const expirationTime = tokenResult.expirationTime
  if (token) {
    log.debug(
      `[refreshFirebaseToken] new token ${token.substring(token.length - 20)} expiring at ${dayjs(tokenResult.expirationTime).format(`YYYY-MM-DD HH:mm:ss`)} (${tokenResult.expirationTime} UTC) (now: ${dayjs().format(`YYYY-MM-DD HH:mm:ss`)})`
    )
    store.dispatch(setToken({ token, expirationTime }))
  }
  return { token, expirationTime }
}

function getAxiosInstance(): AxiosInstance {
  const axiosInstance = axios.create()

  axiosInstance.interceptors.request.use(
    (request) => {
      if (token) {
        request.headers['Authorization'] = `Bearer ${token}`
      } else {
        request.headers['Authorization'] = `Bearer force refresh`
      }
      return request
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      log.error(`[interceptors.response.use] error`, 'message' in error ? error.message : 'none')
      if (error.code === 'ERR_NETWORK') {
        return Promise.reject(error)
      }

      const originalRequest = error.config
      if (error.response.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true // Mark the request as retried to avoid infinite loops.
        try {
          const data = await refreshFirebaseToken()
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${data.token}`
          return axiosInstance(originalRequest) // Retry the original request with the new access token.
        } catch (refreshError) {
          log.error('[response.use.interceptor] Token refresh failed:', refreshError)
          store.dispatch(clearToken())
          await auth.signOut()
          return Promise.reject(refreshError)
        }
      }
      log.warn(
        `[response.use.interceptor] not 403 error (got ${error.response.status}) or is retry (got ${!originalRequest._retry}) for url ${JSON.stringify(originalRequest.url)}`
      )
      return Promise.reject(error) // For all other errors, return the error as is.
    }
  )

  return axiosInstance
}

const createActivitiesApi = (): CategoriesActivitiesApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const activitiesApi = new CategoriesActivitiesApi(config, BASE_URL, axiosInstance)
  return activitiesApi
}

const createDashboardRolesApi = (): DashboardRolesApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const dashboardRolesApi = new DashboardRolesApi(config, BASE_URL, axiosInstance)
  return dashboardRolesApi
}

const createUsersApi = (): UsersApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const usersApi = new UsersApi(config, BASE_URL, axiosInstance)
  return usersApi
}

const createEventApi = (): EventsApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const eventsApi = new EventsApi(config, BASE_URL, axiosInstance)
  return eventsApi
}

const createCategoriesActivitiesApi = (): CategoriesActivitiesApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const categoriesActivitiesApi = new CategoriesActivitiesApi(config, BASE_URL, axiosInstance)
  return categoriesActivitiesApi
}

const createMultipartServicesApi = (): IMultipartServicesApi => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const categoriesActivitiesApi = new MultipartServicesApi(config, BASE_URL, axiosInstance)
  return categoriesActivitiesApi
}

const createExportApi = (): ExportApiInterface => {
  const config = new Configuration()
  const axiosInstance = getAxiosInstance()
  const eventsApi = new ExportApi(config, BASE_URL, axiosInstance)
  return eventsApi
}

export {
  createExportApi,
  createDashboardRolesApi,
  createActivitiesApi,
  createUsersApi,
  createEventApi,
  createCategoriesActivitiesApi,
  createMultipartServicesApi
}
